import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { UserService } from 'src/app/shared/services/user-services/user.service';
import { environment } from 'src/environments/environment';

interface DocumentRequirement {
  id: number;
  document_type: string;
  document_name: string;
  is_mandatory: boolean;
  description?: string;
  max_file_size?: number;
  allowed_file_types?: string[];
  status?: string;
}

interface UploadedDocument {
  id: number;
  staff_id: number;
  document_type: string;
  document_name: string;
  document_number?: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  expiry_date?: string;
  verification_status: string;
  verified_by_name?: string;
  verified_at?: string;
  rejection_reason?: string;
  created_at: string;
}

@Component({
  selector: 'app-identity-documents',
  templateUrl: './identity-documents.component.html',
  styleUrls: ['./identity-documents.component.css']
})
export class IdentityDocumentsComponent implements OnInit {
  imageBaseUrl = environment.imageBaseUrl;
  documentBaseUrl = `${environment.imageBaseUrl}identity-documents/`;
  
  staffId: number | null = null;
  userRole: string = '';
  isAdmin: boolean = false;
  
  requirements: DocumentRequirement[] = [];
  uploadedDocuments: UploadedDocument[] = [];
  documentStatus: any = null;
  
  loading: boolean = false;
  uploading: boolean = false;
  
  // Upload form data
  selectedDocumentType: string = '';
  selectedFile: File | null = null;
  documentNumber: string = '';
  expiryDate: string = '';
  
  // File preview
  previewUrl: string | null = null;

  constructor(
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    private userService: UserService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.userRole = this.userService.getUserRole();
    this.isAdmin = this.userRole === 'Admin' || this.userRole === 'Organization';
    
    // Get staff ID from route or user session
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.staffId = parseInt(params['id']);
      } else {
        // Get from user session for staff viewing their own documents
        const userDetails = this.userService.getUserDetailsFromSession();
        this.staffId = userDetails?.id || null;
      }
      
      if (this.staffId) {
        this.loadData();
      }
    });
  }

  loadData(): void {
    this.loading = true;
    
    // Load document requirements
    this.apiService.getIdentityDocumentRequirements().subscribe({
      next: (res: any) => {
        if (res.success) {
          this.requirements = res.data;
        }
      },
      error: (err) => {
        console.error('Error loading requirements:', err);
      }
    });
    
    // Load uploaded documents
    this.apiService.getStaffIdentityDocuments(this.staffId || undefined).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.uploadedDocuments = res.data;
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading documents:', err);
        this.loading = false;
      }
    });
    
    // Load document status
    this.apiService.getIdentityDocumentStatus(this.staffId || undefined).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.documentStatus = res.data;
        }
      },
      error: (err) => {
        console.error('Error loading document status:', err);
      }
    });
  }

  getDocumentByType(type: string): UploadedDocument | undefined {
    return this.uploadedDocuments.find(d => d.document_type === type);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'not_uploaded':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'verified':
        return 'check_circle';
      case 'pending':
        return 'hourglass_empty';
      case 'rejected':
        return 'cancel';
      case 'not_uploaded':
      default:
        return 'upload_file';
    }
  }

  onFileSelected(event: any, docType: string): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        this.toastr.error('File size must be less than 5MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        this.toastr.error('Only PDF, JPG, and PNG files are allowed');
        return;
      }
      
      this.selectedFile = file;
      this.selectedDocumentType = docType;
      
      // Generate preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          this.previewUrl = reader.result as string;
        };
        reader.readAsDataURL(file);
      } else {
        this.previewUrl = null;
      }
    }
  }

  uploadDocument(): void {
    if (!this.selectedFile || !this.selectedDocumentType) {
      this.toastr.error('Please select a file to upload');
      return;
    }
    
    const requirement = this.requirements.find(r => r.document_type === this.selectedDocumentType);
    if (!requirement) {
      this.toastr.error('Invalid document type');
      return;
    }
    
    this.uploading = true;
    
    const data = {
      document_type: this.selectedDocumentType,
      document_name: requirement.document_name,
      document_number: this.documentNumber || null,
      expiry_date: this.expiryDate || null,
      staff_id: this.staffId
    };
    
    this.apiService.uploadIdentityDocument(this.selectedFile, data).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastr.success(res.message || 'Document uploaded successfully');
          this.resetUploadForm();
          this.loadData();
        } else {
          this.toastr.error(res.message || 'Failed to upload document');
        }
        this.uploading = false;
      },
      error: (err) => {
        console.error('Upload error:', err);
        this.toastr.error(err.error?.message || 'Failed to upload document');
        this.uploading = false;
      }
    });
  }

  deleteDocument(doc: UploadedDocument): void {
    if (!confirm(`Are you sure you want to delete ${doc.document_name}?`)) {
      return;
    }
    
    this.apiService.deleteIdentityDocument(doc.id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastr.success('Document deleted successfully');
          this.loadData();
        } else {
          this.toastr.error(res.message || 'Failed to delete document');
        }
      },
      error: (err) => {
        console.error('Delete error:', err);
        this.toastr.error(err.error?.message || 'Failed to delete document');
      }
    });
  }

  verifyDocument(doc: UploadedDocument, status: 'verified' | 'rejected'): void {
    let rejectionReason = '';
    
    if (status === 'rejected') {
      rejectionReason = prompt('Please provide a reason for rejection:') || '';
      if (!rejectionReason) {
        this.toastr.error('Rejection reason is required');
        return;
      }
    }
    
    this.apiService.verifyIdentityDocument(doc.id, {
      verification_status: status,
      rejection_reason: rejectionReason
    }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastr.success(`Document ${status}`);
          this.loadData();
        } else {
          this.toastr.error(res.message || 'Failed to verify document');
        }
      },
      error: (err) => {
        console.error('Verify error:', err);
        this.toastr.error(err.error?.message || 'Failed to verify document');
      }
    });
  }

  viewDocument(doc: UploadedDocument): void {
    const url = this.documentBaseUrl + doc.file_name;
    window.open(url, '_blank');
  }

  resetUploadForm(): void {
    this.selectedFile = null;
    this.selectedDocumentType = '';
    this.documentNumber = '';
    this.expiryDate = '';
    this.previewUrl = null;
    
    // Reset file inputs
    const fileInputs = document.querySelectorAll('input[type="file"]') as NodeListOf<HTMLInputElement>;
    fileInputs.forEach(input => input.value = '');
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  needsDocumentNumber(type: string): boolean {
    return ['aadhar', 'pan', 'passport', 'driving_license', 'voter_id'].includes(type);
  }

  needsExpiryDate(type: string): boolean {
    return ['passport', 'driving_license'].includes(type);
  }

  getProgressPercentage(): number {
    if (!this.documentStatus) return 0;
    const total = this.documentStatus.totalMandatory || 1;
    const uploaded = this.documentStatus.uploadedMandatory || 0;
    return Math.round((uploaded / total) * 100);
  }
}
